import type { PaymentWidget } from '@oneapp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/icons';
import { Sheet } from '../components/Sheet';
import { Button, Callout } from '../components/ui';
import { Markdown } from './markdown';

/** Copies text, and says so for a couple of seconds (for screen readers too). */
function useCopy(): [boolean, (text: string) => void] {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = (text: string) => {
    void navigator.clipboard
      ?.writeText(text)
      .then(() => setCopied(true))
      .catch(() => {});
  };
  return [copied, copy];
}

/**
 * One detail to copy into a bank's form (S15, and C5's card): its label above, the value (fixed-width
 * for digits and codes), and a copy button beside it.
 */
export function CopyRow({
  label,
  value,
  copyLabel,
  mono,
}: {
  label: string;
  value: string;
  copyLabel: string;
  mono?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const [copied, copy] = useCopy();
  return (
    <div className="copyrow">
      <span className="copyrow__text">
        <span className="copyrow__label">{label}</span>
        <span className={`copyrow__value${mono ? ' copyrow__value--mono' : ''}`}>{value}</span>
      </span>
      <button
        type="button"
        className="iconbtn copyrow__button"
        aria-label={copyLabel}
        // The account number is copied without the spaces it's shown with.
        onClick={() => copy(mono ? value.replace(/\s+/g, '') : value)}
      >
        <Icon name={copied ? 'check' : 'copy'} size={20} />
      </button>
      <span className="visually-hidden" role="status">
        {copied ? t('chat.copied') : ''}
      </span>
    </div>
  );
}

/**
 * S15: a GST challan to pay at the bank by NEFT or RTGS. The exact amount, how to pay it from the
 * challan's bank, each detail the bank's form asks for (to copy), what mustn't change, where the money
 * goes; then "I've paid" (the chat watches for RBI's word) or "Copy all the details".
 */
export function PaymentSheet({
  widget,
  onClose,
  onPaid,
}: {
  widget: PaymentWidget;
  onClose: () => void;
  onPaid: () => void;
}) {
  const { t } = useTranslation();
  const [copied, copy] = useCopy();
  return (
    <Sheet
      open
      onClose={onClose}
      label={widget.eyebrow}
      secure
      footer={
        <div className="pay__actions">
          <Button onClick={onPaid}>{widget.submitLabel}</Button>
          {widget.copyAll ? (
            <Button variant="secondary" onClick={() => copy(widget.copyAll?.text ?? '')}>
              <Icon name={copied ? 'check' : 'copy'} size={18} />
              {copied ? t('chat.copied') : widget.copyAll.label}
            </Button>
          ) : null}
        </div>
      }
    >
      <h2 className="visually-hidden">{widget.title}</h2>
      <div className="pay__amount">
        <span className="pay__amount-label">{widget.amountLabel}</span>
        <span className="pay__amount-value">{widget.amount}</span>
        {widget.amountHint ? <span className="pay__amount-hint">{widget.amountHint}</span> : null}
      </div>
      <div className="pay__how">
        <Markdown text={widget.instructions} />
      </div>
      <div className="pay__details">
        {widget.details.map((detail) => (
          <CopyRow key={detail.label} {...detail} />
        ))}
      </div>
      {widget.warning ? (
        <Callout tone="amber" icon="alert-triangle">
          {widget.warning}
        </Callout>
      ) : null}
      {widget.footnote ? <Callout icon="landmark">{widget.footnote}</Callout> : null}
      <span className="visually-hidden" role="status">
        {copied ? t('chat.copied') : ''}
      </span>
    </Sheet>
  );
}
